import dayjs, { QUnitType } from 'dayjs';
import styled, { css } from 'styled-components';
import { getBookingDurationLabel } from 'utils/dayjs/bookingDuration';
import ViewSelectedAddonsModal from 'components/modals/ViewSelectedAddonsModal';
import useModal from 'components/remodal/v2/useModal';
import BookingBadge from 'components/ui/BookingBadge/BookingBadge';
import { FlexAlignCenterSpaceBetween, FlexCol } from '../../primitives/flex';
import { Text } from '../../primitives/typography';
import { bookingStatus } from '../../../stacks/messaging/helpers';
import useConversationDetail from '../hooks/useConversationDetail';

const BookingInfo = () => {
  const { data } = useConversationDetail();

  const {
    booking: { boat = {}, state, trip_type, package_type, passengers, booking_add_ons, renter_add_on_total, user_type },
    booking,
    booking_modification: bookingModifications,
    state_read_by_user: stateReadByUser,
  } = data;

  const tripStart = dayjs(booking.trip_start).tz(boat.time_zone);
  const tripFinish = dayjs(booking.trip_finish).tz(boat.time_zone);

  const getTripDiff = (unit: QUnitType = 'hours') => tripFinish.diff(tripStart, unit);

  const timeParts = [tripStart.format('h:mm A'), ' - ', tripFinish.format('h:mm A')];

  const multiDayPart = (momentTime) => [momentTime.format('MMM DD, YYYY'), '•', momentTime.format('h:mm A')];

  const dateParts = [tripStart.format('MMM DD, YYYY'), getTripDiff() > 8 ? tripFinish.format('MMM DD, YYYY') : null];

  const dateValue = dateParts.filter((d) => !!d).join(' ');

  const { modal: addonsModal, openModal: openAddonsModal } = useModal(
    <ViewSelectedAddonsModal addonList={booking_add_ons} totalCents={renter_add_on_total} />,
    {
      size: 'medium',
      trackingName: 'view_selected_addons_modal',
    }
  );

  const infoItems = [
    {
      label: 'Status',
      value: (
        <BookingBadge
          bookingStatus={bookingStatus(state, booking.captain_state, booking.captain_state, stateReadByUser)}
          bookingModification={bookingModifications?.can_accept_or_decline}
        />
      ),
    },
    {
      label: trip_type === 'multi_day' ? 'Start date' : 'Date',
      value: trip_type === 'multi_day' ? multiDayPart(tripStart).join(' ') : dateValue,
      secondValue: trip_type !== 'multi_day' ? timeParts : null,
      override: bookingModifications
        ? {
            value: dayjs(bookingModifications.trip_start).tz(boat.time_zone).format('MMM DD, YYYY'),
            secondValue: `${dayjs(bookingModifications.trip_start).tz(boat.time_zone).format('h:mm A')} - ${dayjs(
              bookingModifications.trip_finish
            )
              .tz(boat.time_zone)
              .format('h:mm A')}`,
          }
        : null,
    },
    {
      label: 'End date',
      value: trip_type === 'multi_day' ? multiDayPart(tripFinish).join(' ') : null,
    },
    {
      label: 'Duration',
      value: getBookingDurationLabel(tripStart, tripFinish),
    },
    {
      label: 'Captain',
      value: package_type === 'captained' ? 'Captained' : 'No captain',
    },
    {
      label: 'Passengers',
      value: passengers,
    },
    booking_add_ons &&
      booking_add_ons.length > 0 && {
        label: 'Add-ons',
        color: user_type === 'renter' ? 'rgb(7, 81, 194)' : '#0e7873',
        value: `${booking_add_ons.length} selected`,
        handler: openAddonsModal,
      },
  ].filter((b) => !!b.value);

  return (
    <FlexCol gap="10px">
      {infoItems.map((item, index) => (
        <FlexAlignCenterSpaceBetween key={index}>
          <Text as="span">{item.label}</Text>
          <ValueWrapper onClick={item.handler}>
            <div>
              <Text
                as="span"
                textDecoration={item.override ? 'line-through' : 'initial'}
                color={item.override ? '#8E9697' : item.color || 'inherit'}
                fWeight={600}
              >
                {item.value}
              </Text>
              {item.secondValue && (
                <Text
                  as="span"
                  textDecoration={item.override ? 'line-through' : 'initial'}
                  color={item.override ? '#8E9697' : 'inherit'}
                  fWeight={600}
                >
                  <Dot />
                  {item.secondValue}
                </Text>
              )}
            </div>
            {item.override && (
              <div>
                <Text as="span" fWeight={600}>
                  {item.override.value}
                </Text>
                {item.override.secondValue && (
                  <Text as="span" fWeight={600}>
                    <Dot />
                    {item.override.secondValue}
                  </Text>
                )}
              </div>
            )}
          </ValueWrapper>
        </FlexAlignCenterSpaceBetween>
      ))}

      {addonsModal}
    </FlexCol>
  );
};

const Dot = styled((props) => <span {...props}>&nbsp;•&nbsp;</span>)``;

const ValueWrapper = styled(FlexCol)`
  justify-items: end;
  justify-content: end;
  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}
`;

export default BookingInfo;
